import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import {
  Search,
  RotateCcw,
  SlidersHorizontal,
  ChevronDown,
} from "lucide-react";
import { cn } from "../../lib/utils";
import {
  categoriesAPI,
  statesAPI,
  districtsAPI,
  type Category,
  type State,
  type District,
  type City,
} from "../../services/api";
import {
  VENDOR_FILTER_ALL,
  countActivePanelFilters,
  countActiveVendorFilters,
  type VendorListFilterValues,
} from "./vendorFilterUtils";

function parseMasterList<T>(res: unknown): T[] {
  if (Array.isArray(res)) {
    return res;
  }
  if (res && typeof res === "object") {
    const data = res as { results?: T[]; data?: T[] };
    const list = data.results ?? data.data ?? [];
    return Array.isArray(list) ? list : [];
  }
  return [];
}

interface VendorListFiltersProps {
  filters: VendorListFilterValues;
  onFiltersChange: (next: VendorListFilterValues) => void;
  /** Query string fields for vendorsAPI.list (excluding page / page_size) */
  onApply: (queryFilters: Record<string, string>) => void;
  onClear: () => void;
  loading?: boolean;
}

export function VendorListFilters({
  filters,
  onFiltersChange,
  onApply,
  onClear,
  loading = false,
}: VendorListFiltersProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [catRes, stateList] = await Promise.all([
          categoriesAPI.list({ page_size: 500, is_active: true }),
          statesAPI.list(),
        ]);
        setCategories(parseMasterList<Category>(catRes));
        setStates(Array.isArray(stateList) ? stateList : []);
      } catch {
        setCategories([]);
        setStates([]);
      }
    };
    void loadOptions();
  }, []);

  useEffect(() => {
    if (filters.stateId === VENDOR_FILTER_ALL) {
      setDistricts([]);
      return;
    }
    let cancelled = false;
    setLoadingDistricts(true);
    void statesAPI
      .getDistrictsByStateId(filters.stateId)
      .then(list => {
        if (!cancelled) {
          setDistricts(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDistricts([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDistricts(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters.stateId]);

  useEffect(() => {
    if (filters.districtId === VENDOR_FILTER_ALL) {
      setCities([]);
      return;
    }
    let cancelled = false;
    setLoadingCities(true);
    void districtsAPI
      .getCitiesByDistrictId(filters.districtId)
      .then(list => {
        if (!cancelled) {
          setCities(list);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCities([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingCities(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters.districtId]);

  const patch = (partial: Partial<VendorListFilterValues>) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const buildQueryFilters = (): Record<string, string> => {
    const params: Record<string, string> = {};
    const search = filters.search.trim();
    if (search) {
      params.search = search;
    }
    if (filters.categoryId !== VENDOR_FILTER_ALL) {
      params.vendor_category = filters.categoryId;
      const cat = categories.find(c => String(c.id) === filters.categoryId);
      if (cat?.name) {
        params.category = cat.name;
      }
    }
    if (filters.vendorType !== VENDOR_FILTER_ALL) {
      params.vendor_type = filters.vendorType;
    }
    if (filters.stateId !== VENDOR_FILTER_ALL) {
      const state = states.find(s => String(s.id) === filters.stateId);
      if (state?.name) {
        params.state = state.name;
      }
    }
    if (filters.districtId !== VENDOR_FILTER_ALL) {
      const district = districts.find(d => String(d.id) === filters.districtId);
      if (district?.name) {
        params.district = district.name;
      }
    }
    if (filters.cityId !== VENDOR_FILTER_ALL) {
      const city = cities.find(c => String(c.id) === filters.cityId);
      if (city?.name) {
        params.city = city.name;
      }
    }
    if (filters.verificationStatus !== VENDOR_FILTER_ALL) {
      params.verification_status = filters.verificationStatus;
    }
    if (filters.availableForWork === "yes") {
      params.available_for_work = "true";
    } else if (filters.availableForWork === "no") {
      params.available_for_work = "false";
    }
    return params;
  };

  const panelFilterCount = countActivePanelFilters(filters);
  const hasActiveFilters = countActiveVendorFilters(filters) > 0;

  const handleApply = () => {
    onApply(buildQueryFilters());
    setFiltersExpanded(false);
  };

  return (
    <Collapsible open={filtersExpanded} onOpenChange={setFiltersExpanded}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="vendor-filter-search"
            placeholder="Search by vendor name, company, contact..."
            value={filters.search}
            onChange={e => patch({ search: e.target.value })}
            onKeyDown={e => e.key === "Enter" && handleApply()}
            className="h-10 border-input bg-background pl-10 shadow-sm"
            aria-label="Search vendors"
          />
        </div>

        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 gap-2 border-zinc-200 bg-white px-4 font-semibold shadow-sm hover:bg-zinc-50 hover:text-[#8B3232]"
            aria-expanded={filtersExpanded}
            aria-controls="vendor-list-filters-panel"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-[#FCECEC]">
              <SlidersHorizontal className="h-4 w-4 text-[#8B3232]" />
            </span>
            Filters
            {panelFilterCount > 0 && (
              <Badge className="h-5 min-w-5 border-0 bg-[#8B3232] px-1.5 text-[11px] text-white hover:bg-[#8B3232]">
                {panelFilterCount}
              </Badge>
            )}
            <ChevronDown
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform duration-200",
                filtersExpanded && "rotate-180"
              )}
            />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent
        id="vendor-list-filters-panel"
        className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 overflow-visible"
      >
        <Card className="mt-3 overflow-visible border-zinc-200/90 shadow-sm">
          <CardHeader className="space-y-1 pb-2">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#FCECEC]">
                <SlidersHorizontal className="h-4 w-4 text-[#8B3232]" />
              </span>
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-visible pt-0">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:items-end lg:gap-x-5">
              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-category"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Category
                </Label>
                <Select
                  value={filters.categoryId}
                  onValueChange={v => patch({ categoryId: v })}
                >
                  <SelectTrigger
                    id="vendor-filter-category"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>
                      All categories
                    </SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-type"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Vendor type
                </Label>
                <Select
                  value={filters.vendorType}
                  onValueChange={v => patch({ vendorType: v })}
                >
                  <SelectTrigger
                    id="vendor-filter-type"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>All types</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-verification"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Verification status
                </Label>
                <Select
                  value={filters.verificationStatus}
                  onValueChange={v => patch({ verificationStatus: v })}
                >
                  <SelectTrigger
                    id="vendor-filter-verification"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>
                      All statuses
                    </SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-available"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Available for work
                </Label>
                <Select
                  value={filters.availableForWork}
                  onValueChange={v => patch({ availableForWork: v })}
                >
                  <SelectTrigger
                    id="vendor-filter-available"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>All</SelectItem>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-state"
                  className="text-xs font-medium text-muted-foreground"
                >
                  State
                </Label>
                <Select
                  value={filters.stateId}
                  onValueChange={v =>
                    patch({
                      stateId: v,
                      districtId: VENDOR_FILTER_ALL,
                      cityId: VENDOR_FILTER_ALL,
                    })
                  }
                >
                  <SelectTrigger
                    id="vendor-filter-state"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue placeholder="All states" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>
                      All states
                    </SelectItem>
                    {states.map(st => (
                      <SelectItem key={st.id} value={String(st.id)}>
                        {st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-district"
                  className="text-xs font-medium text-muted-foreground"
                >
                  District
                </Label>
                <Select
                  value={filters.districtId}
                  onValueChange={v =>
                    patch({
                      districtId: v,
                      cityId: VENDOR_FILTER_ALL,
                    })
                  }
                  disabled={
                    filters.stateId === VENDOR_FILTER_ALL || loadingDistricts
                  }
                >
                  <SelectTrigger
                    id="vendor-filter-district"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue
                      placeholder={
                        filters.stateId === VENDOR_FILTER_ALL
                          ? "Select state first"
                          : loadingDistricts
                            ? "Loading districts..."
                            : "All districts"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>
                      All districts
                    </SelectItem>
                    {districts.map(district => (
                      <SelectItem key={district.id} value={String(district.id)}>
                        {district.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-0 space-y-1.5">
                <Label
                  htmlFor="vendor-filter-city"
                  className="text-xs font-medium text-muted-foreground"
                >
                  City
                </Label>
                <Select
                  value={filters.cityId}
                  onValueChange={v => patch({ cityId: v })}
                  disabled={
                    filters.districtId === VENDOR_FILTER_ALL || loadingCities
                  }
                >
                  <SelectTrigger
                    id="vendor-filter-city"
                    className="h-10 w-full border-input bg-background shadow-sm"
                  >
                    <SelectValue
                      placeholder={
                        filters.stateId === VENDOR_FILTER_ALL
                          ? "Select state first"
                          : filters.districtId === VENDOR_FILTER_ALL
                            ? "Select district first"
                            : loadingCities
                              ? "Loading cities..."
                              : "All cities"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VENDOR_FILTER_ALL}>
                      All cities
                    </SelectItem>
                    {cities.map(city => (
                      <SelectItem key={city.id} value={String(city.id)}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex min-w-0 flex-col justify-end">
                <div className="flex h-10 flex-wrap items-center justify-end gap-2">
                  <Button
                    type="button"
                    className="h-10 shrink-0 gap-2 bg-[#8B3232] px-3 font-semibold text-white hover:bg-[#743030] sm:px-4"
                    onClick={handleApply}
                    disabled={loading}
                  >
                    <Search className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">Apply filters</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 shrink-0 gap-2 px-3 sm:px-4"
                    onClick={onClear}
                    disabled={loading || !hasActiveFilters}
                  >
                    <RotateCcw className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">Clear all</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}
